import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { Fragment, forwardRef, ReactNode, Ref, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { coreTokens } from '../../core';
import { useText } from '../../utils/hooks/use_text';
import { restrictedZIndexes } from '../../utils/restricted_z_indexes';
import { AutoFocus } from '../auto_focus';
import { Button } from '../button';
import { Spacer } from '../layout';
import { NotificationAlert } from '../notification_alert';
import { useCellulaProviderContext } from '../provider';
import { theme } from './dialog_alert.theme';

const overlayShow = keyframes`
	from {
		opacity: 0;
	}
  to {
		opacity: 1;
	}
`;

const contentShow = keyframes`
	from {
		opacity: 0;
		transform: translate(-50%, -40%) scale(0.96);
	}
  to {
		opacity: 1;
		transform: translate(-50%, -50%) scale(1);
	}
`;

const elements = {
	overlay: styled(AlertDialogPrimitive.Overlay)`
		background: rgba(0, 0, 0, 0.3);
		position: fixed;
		inset: 0;
		animation-duration: 500ms;
		animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
		will-change: opacity;
		/* Needs to use a high z-index to have it appear on top of other elements */
		z-index: ${restrictedZIndexes.DANGER};

		@media (prefers-reduced-motion: no-preference) {
			animation-name: ${overlayShow};
		}
	`,
	close: styled.div`
		display: flex;
		justify-content: end;
	`,
	content: styled(AlertDialogPrimitive.Content)<{ $widthMode: DialogAlertWidthMode }>`
		background: ${theme.getValue('background')};
		border-radius: ${theme.getValue('borderRadius')};
		box-shadow: ${theme.getValue('boxShadow')};
		position: fixed;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: ${({ $widthMode }) => ($widthMode === 'content' ? 'fit-content' : '90vw')};
		max-width: ${({ $widthMode }) => ($widthMode === 'content' ? 'calc(100vw - 32px)' : theme.getValue('maxWidth'))};
		max-height: 85vh;
		padding: ${theme.getValue('padding')};
		animation-duration: 250ms;
		animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
		/* Needs to use a high z-index to have it appear on top of other elements */
		z-index: ${restrictedZIndexes.DANGER};

		&:focus {
			outline: none;
		}
		@media (prefers-reduced-motion: no-preference) {
			animation-name: ${contentShow};
		}
	`,
	inner: styled.div`
		display: flex;
		flex-direction: column;
		padding: ${theme.getValue('innerPadding')};
	`,
	header: styled(AlertDialogPrimitive.Title)`
		font: ${theme.getValue('headerText')};
		margin: 0;

		${coreTokens.breakpointQuery.medium} {
			font: ${theme.getValue('headerTextAtMediumAndAbove')};
		}
	`,
	body: styled(AlertDialogPrimitive.Description)`
		font: ${theme.getValue('bodyText')};
		margin: 0;
		white-space: pre-line;

		${coreTokens.breakpointQuery.medium} {
			font: ${theme.getValue('bodyTextAtMediumAndAbove')};
		}
	`,
	buttonGroup: styled.div`
		display: flex;
		justify-content: stretch;
		gap: ${theme.getValue('buttonGroupGap')};
		flex-direction: column-reverse;

		& > * {
			width: 100%;
		}

		${coreTokens.breakpointQuery.small} {
			justify-content: flex-end;
			flex-direction: row;

			& > * {
				width: auto;
			}
		}
	`,
};

interface WithSuccessPropsUnlocked {
	/**
	 * Callback when clicking the success button.
	 * When a promise is returned, the dialog will show a loading state.
	 * If a DialogAlertSuccessError is thrown, the dialog will show an error message.
	 */
	onSuccess(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void | Promise<void>;

	/**
	 * The text of the success button
	 */
	successText: string;

	/**
	 * The visual representation of the success button.
	 */
	successButtonVariant?: 'primary' | 'danger';
}
interface WithSuccessPropsLocked {
	onSuccess?: never;
	successText?: never;
	successButtonVariant?: never;
}
type WithSuccessProps = WithSuccessPropsUnlocked | WithSuccessPropsLocked;

interface WithTrigger {
	/**
	 * The element who will trigger the dialog.
	 * E.g. `<button>Click me</button>`
	 */
	trigger: ReactNode;

	/**
	 * Use to set the dialog as open or not.
	 */
	isOpen?: never;
}
interface WithoutTrigger {
	trigger?: never;
	isOpen: boolean;
}
type WithTriggerProps = WithTrigger | WithoutTrigger;

type DialogAlertWidthMode = 'default' | 'content';

type DialogAlertProps = {
	/**
	 * Header/title of the dialog.
	 */
	header: string;

	/**
	 * Body content of the dialog displayed with given font style.
	 */
	description: string;

	/**
	 * Raw body content of the dialog.
	 */
	children?: ReactNode;

	/**
	 * Use to open the dialog when render.
	 */
	defaultOpen?: boolean;

	/**
	 * Text of the abort button.
	 */
	abortText: string;

	/**
	 * Callback when clicking the abort button, closing the modal without taking action.
	 */
	onAbort?(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
	onOpenChange?(open: boolean): void;

	/**
	 * Controls the dialog width behavior.
	 * `default` keeps the current max-width dialog sizing.
	 * `content` allows the dialog to shrink to fit its content, up to the themed max width.
	 */
	widthMode?: DialogAlertWidthMode;
} & WithSuccessProps &
	WithTriggerProps;

function CellulaDialogAlert(
	{
		header,
		description,
		children,
		defaultOpen,
		trigger,
		isOpen: controlledIsOpen,
		successText,
		abortText,
		successButtonVariant,
		onAbort,
		onSuccess,
		onOpenChange,
		widthMode = 'default',
	}: DialogAlertProps,
	ref: Ref<HTMLDivElement>,
): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const [open, setOpen] = useState(false);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const [loading, setLoading] = useState(false);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const blockOpenChangeOnce = useRef(false);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const staticTexts = useText();
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const { portalElement } = useCellulaProviderContext();

	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	useEffect(
		function whenControlledIsOpenChanges() {
			if (controlledIsOpen !== open) {
				setOpen(controlledIsOpen ?? open);
			}
		},
		[controlledIsOpen, open],
	);

	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	useEffect(
		function whenOpenIsChanged() {
			if (!open) {
				setErrorMessage(null);
			}
		},
		[open],
	);

	function handleOpenChange(event: boolean): void {
		if (loading) {
			return;
		}
		if (blockOpenChangeOnce.current) {
			blockOpenChangeOnce.current = false;

			return;
		}

		setOpen(event);
		onOpenChange?.(event);
	}

	function handleSuccess(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
		const result = onSuccess != null ? onSuccess(event) : void 0;
		blockOpenChangeOnce.current = true;
		setErrorMessage(null);

		if (result instanceof Promise) {
			setLoading(true);

			result
				.then(() => {
					setOpen(false);
				})
				.catch((error: unknown) => {
					if (error instanceof DialogAlertSuccessError) {
						setErrorMessage(error.message);

						return;
					}

					setErrorMessage(staticTexts('Common.UnknownError'));
				})
				.finally(() => {
					setLoading(false);
				});

			return;
		}

		setOpen(false);
	}

	function handleErrorClose(): void {
		setErrorMessage(null);
	}

	return (
		<AlertDialogPrimitive.Root defaultOpen={defaultOpen} open={open} onOpenChange={handleOpenChange}>
			{trigger != null ? <AlertDialogPrimitive.Trigger asChild>{trigger}</AlertDialogPrimitive.Trigger> : null}

			<AlertDialogPrimitive.Portal container={portalElement}>
				<elements.overlay />

				<elements.content ref={ref} $widthMode={widthMode}>
					<elements.inner>
						<elements.header>{header}</elements.header>
						<Spacer vertical={theme.getValue('headerMarginBottom')} />
						<elements.body>{description}</elements.body>
						{children ? (
							<Fragment>
								<Spacer vertical={theme.getValue('headerMarginBottom')} />
								{children}
							</Fragment>
						) : null}
						<Spacer vertical={theme.getValue('bodyMarginBottom')} />

						<AnimatePresence>
							{errorMessage != null ? (
								<motion.div
									initial={{
										height: 0,
										opacity: 0,
									}}
									animate={{
										height: 'auto',
										opacity: 1,
									}}
									exit={{
										height: 0,
										opacity: 0,
										transition: {
											duration: 0.1,
										},
									}}
									style={{ overflow: 'hidden' }}
								>
									<NotificationAlert closable onClose={handleErrorClose} variant='danger'>
										<AutoFocus>{errorMessage}</AutoFocus>
									</NotificationAlert>
									<Spacer vertical={theme.getValue('errorMarginBottom')} />
								</motion.div>
							) : null}
						</AnimatePresence>

						<elements.buttonGroup>
							<AlertDialogPrimitive.Cancel asChild>
								<Button
									onClick={onAbort}
									variant='ghost'
									size='medium'
									disabled={loading}
									disabledText={staticTexts('Component.DialogAlert.ButtonAbort.DisabledText')}
								>
									{abortText}
								</Button>
							</AlertDialogPrimitive.Cancel>

							{successText != null ? (
								<AlertDialogPrimitive.Action asChild>
									<Button onClick={handleSuccess} loading={loading} variant={successButtonVariant ?? 'danger'} size='medium'>
										{successText}
									</Button>
								</AlertDialogPrimitive.Action>
							) : null}
						</elements.buttonGroup>
					</elements.inner>
				</elements.content>
			</AlertDialogPrimitive.Portal>
		</AlertDialogPrimitive.Root>
	);
}

class DialogAlertSuccessError extends Error {
	public readonly message: string;

	constructor(message: string) {
		super('Should be handled Success Error');
		this.name = 'InvalidAnalyticConfig';
		this.message = message;
		Object.setPrototypeOf(this, DialogAlertSuccessError.prototype);
	}
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component for interrupt the user with important content that requires a
 * action from the user.
 */
const DialogAlert = forwardRef(CellulaDialogAlert);

export type { DialogAlertProps };
export { DialogAlert, DialogAlertSuccessError };
